import IconUser16 from '@warp-ds/icons/react/user-16';
import cn from 'classnames';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SimpleHead } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { SortDetails } from 'shared/page/SortDetails';
import { Dropdown } from 'shared/search/common/Dropdown';
import { PaxCombination } from 'shared/search/prepackage/Pax/PaxCombination';
import { formatPaxTriggerLabel } from 'shared/search/prepackage/Pax/PaxUtils';
import type { PaleState, ThunkDispatchFunc } from '@/interfaces';
import { actions } from '@/state/filterActions';
import { fetchResults } from '@/state/resultActions';
import { DestinationFilter } from './filters/checkbox/DestinationFilter';
import CurrentFilters from './filters/currentFilters/CurrentFilters';
import { FilterGroup } from './filters/FilterGroup';
import { Filters } from './filters/Filters';
import { OriginDropdown } from './filters/origin/OriginDropdown';
import ResultTravelTimePicker from './filters/travelTime/ResultTravelTimePicker';
import { TopBar } from './resultList/components/topBar/TopBar';
import { ResultList } from './resultList/ResultList';

export const ResultPageLayout = () => {
    const dispatch = useDispatch<ThunkDispatchFunc>();

    const filtersOpen = useSelector((s: PaleState) => s.appState.filtersOpen);
    const numberOfAdults = useSelector((s: PaleState) => s.filters.numberOfAdults);
    const agesOfChildren = useSelector((s: PaleState) => s.filters.agesOfChildren);
    const origin = useSelector((s: PaleState) => s.filters.origin);

    const updatePax = useCallback(
        (adults: number, ages: number[]) => {
            dispatch(actions.changePaxCombination({ numberOfAdults: adults, agesOfChildren: ages }));
            dispatch(fetchResults());
        },
        [dispatch],
    );

    const changeOrigin = useCallback(
        (newOrigin: string) => {
            dispatch(actions.changeOrigin(newOrigin));
            dispatch(fetchResults());
        },
        [dispatch],
    );

    return (
        <>
            <SimpleHead
                title="Pakkereiser - sammenlign pris | FINN reise"
                canonical="https://www.finn.no/reise/pakkereiser/resultat"
                description="Finn billige pakkereiser fra alle reisebyrå på ett sted! På FINN.no kan du sammenligne priser og spare penger."
            />
            <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/pakkereiser">
                <TopBar />
                <h1 className="sr-only">Pakkereiser</h1>
                <div className="md:grid md:grid-cols-3 md:gap-32 pb-32">
                    <div className={cn('divide-y-1', { 'hidden md:block': !filtersOpen })}>
                        <div test-id="origin" className="p-16 md:pt-0">
                            <h3 className="h4 mb-16">Utreise og dato</h3>
                            <OriginDropdown selectedValue={origin} filterChange={changeOrigin} />
                            <h3 className="sr-only">Alternativer for avreise og hjemreise</h3>
                            <ResultTravelTimePicker />
                            <Dropdown
                                trigger={{
                                    text: formatPaxTriggerLabel(numberOfAdults, agesOfChildren),
                                    label: 'Reisende',
                                }}
                                prefix={<IconUser16 aria-hidden />}
                                className="mt-16">
                                <PaxCombination numberOfAdults={numberOfAdults} agesOfChildren={agesOfChildren} updatePax={updatePax} />
                            </Dropdown>
                        </div>
                        <FilterGroup title="Reisemål" testId="destination">
                            <DestinationFilter />
                        </FilterGroup>
                        <Filters />
                    </div>
                    <section className={cn('md:col-span-2', { 'hidden md:block': filtersOpen })}>
                        <SortDetails section="pakkereiser" className="md:hidden py-16" />
                        <CurrentFilters />
                        <ResultList />
                    </section>
                </div>
            </Main>
        </>
    );
};
